import React from "react";
import { yupResolver } from "@hookform/resolvers/yup";
import { FormProvider, useForm } from "react-hook-form";
import { Button, Dialog, Form, Toast } from "@sscs/ui";
import { useFileUploadChecks } from "Hooks";
import { useUpdatePackageMutation } from "Services";
import { ExternalPackageInfo, UpdatePackageRequest } from "Types";
import { apiErrorToast } from "Utils";
import * as yup from "Utils/yup";

type ConfigureImportFormData = NonNullable<
  UpdatePackageRequest["body"]["external_catalog"]
>;

const schema: yup.SchemaOf<ConfigureImportFormData> = yup.object({
  automatic_sync: yup.boolean().required(),
});

type ConfigureImportProps = {
  packageId: string;
  packageName: string;
  externalPackage: ExternalPackageInfo;
  isOpen: boolean;
  onClose: () => void;
};

type ConfigureImportFormProps = Omit<ConfigureImportProps, "isOpen">;

const ConfigureImportForm = ({
  packageId,
  packageName,
  externalPackage,
  onClose,
}: ConfigureImportFormProps): React.JSX.Element => {
  const { maxVersions } = useFileUploadChecks("VERSION", null);
  const [updatePackage, { isLoading }] = useUpdatePackageMutation();
  const methods = useForm<ConfigureImportFormData>({
    resolver: yupResolver(schema),
    defaultValues: { automatic_sync: externalPackage.automatic_sync },
  });

  const handleSave = async (data: ConfigureImportFormData): Promise<void> => {
    try {
      await updatePackage({
        packageId,
        body: { external_catalog: data },
      }).unwrap();

      Toast.show({ title: "Import configuration saved", type: "success" });
      onClose();
    } catch (error) {
      apiErrorToast(error, "Could not save import configuration");
    }
  };

  return (
    <>
      <Dialog.Header>
        <Dialog.Header.Title>
          Configure import from <em>{externalPackage.catalog_display_name}</em>{" "}
          catalog
        </Dialog.Header.Title>
        <Dialog.Header.Subtitle>{packageName}</Dialog.Header.Subtitle>
      </Dialog.Header>
      <FormProvider {...methods}>
        <Form.Form onSubmit={methods.handleSubmit(handleSave)}>
          <Dialog.Body>
            <Dialog.Section>
              <Dialog.Section.Content>
                <Form.Checkbox
                  name="automatic_sync"
                  label="Automatically sync new versions"
                  helperText={`New versions are imported automatically as they become available. The package keeps at most ${maxVersions} versions; the oldest are removed once the limit is reached.`}
                />
              </Dialog.Section.Content>
            </Dialog.Section>
          </Dialog.Body>
          <Dialog.Footer>
            <Dialog.Footer.Actions>
              <Button color="secondary" onClick={onClose}>
                Cancel
              </Button>
              <Button type="submit" disabled={isLoading}>
                Save
              </Button>
            </Dialog.Footer.Actions>
          </Dialog.Footer>
        </Form.Form>
      </FormProvider>
    </>
  );
};

const ConfigureImport = ({
  isOpen,
  onClose,
  ...props
}: ConfigureImportProps): React.JSX.Element => (
  <Dialog.Frame isOpen={isOpen} onClose={onClose}>
    <ConfigureImportForm {...props} onClose={onClose} />
  </Dialog.Frame>
);

export default ConfigureImport;
